/**
 * Settings tab of the site detail page: four cards.
 *
 * 1. Site name: inline rename of the title field, reusing the write the
 *    AeroSites rename modal performs (title + updatedAt, merge).
 * 2. Password protection: the figmafolio post-gate model ported exactly from
 *    FormPassword / FolioForm: the field never prefills, hasStoredHash is
 *    derived from encryptedPassword truthiness only, toggle-ON is blocked
 *    with "Set a password first." when the field is empty and no hash
 *    exists, a 6-character minimum is enforced on both Save and toggle-ON,
 *    toggling with a hash is status-only, and writes are always the bcrypt
 *    hash (salt 10) plus isPasswordActive, never the plaintext.
 * 3. Favicon: uploads to the same Storage path figmafolio uses
 *    (favicons/{generatedUrl}.png via uploadFaviconUrl) and writes
 *    faviconUrl to the url doc.
 * 4. Danger zone: the delete flow moved from the AeroSites card menu,
 *    unchanged (confirm modal, batched versions delete, blobs untouched).
 *
 * Html sites additionally keep the site source card (SiteSourceCard) here;
 * for figma sites that card and the breakpoints editor live in the Prototype
 * tab, so the source has exactly one editable home per site type.
 *
 * Password and favicon are gated at Basic, mirroring figmafolio. Site name is
 * available to all users (naming your own site in your own dashboard is not
 * paywalled).
 */
import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import bcrypt from 'bcryptjs';
import firebase, { uploadFaviconUrl } from '../../../firebase';
import { errorLine } from '../../../components/Aero/AeroStatePanel';
import AeroSwitch from '../../../components/Aero/AeroSwitch';
import AeroFeatureGate, { AeroUpsellBanner } from '../../../components/Aero/AeroFeatureGate';
import SiteSourceCard from '../SiteSourceCard';

function SiteSettingsTab({
  user,
  ownerUid,
  site,
  subscriptionType,
  openUpgradeModal,
  fetchSite,
  fetchVersions,
  deploying,
  progress,
  onDrop,
}) {
  const navigate = useNavigate();
  const db = firebase.firestore();
  // Shared sites write to the OWNER's path, not the logged-in admin's.
  const siteRef = db
    .collection('user').doc(ownerUid || user.uid)
    .collection('url').doc(site.id);

  // --- Site name ---
  const [titleValue, setTitleValue] = useState(site.title || site.generatedUrl);
  const [savingTitle, setSavingTitle] = useState(false);

  const saveTitle = async () => {
    const trimmed = titleValue.trim();
    if (!trimmed || savingTitle) return;
    setSavingTitle(true);
    try {
      await siteRef.set({ title: trimmed, updatedAt: new Date() }, { merge: true });
      await fetchSite();
      toast.success('Site name saved.');
    } catch (err) {
      console.error('Error renaming site:', err);
      toast.error('Could not rename site. ' + errorLine(err));
    } finally {
      setSavingTitle(false);
    }
  };

  // --- Password protection (figmafolio post-gate model) ---
  const [isPasswordActive, setIsPasswordActive] = useState(!!site.isPasswordActive);
  // Truthiness only; the stored hash is never read back into the field.
  const [hasStoredHash, setHasStoredHash] = useState(!!site.encryptedPassword);
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [setupError, setSetupError] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const writePasswordFields = async (fields) => {
    await siteRef.set({ ...fields, updatedAt: new Date() }, { merge: true });
    if (fields.encryptedPassword) setHasStoredHash(true);
    await fetchSite();
  };

  const handlePasswordSwitch = async () => {
    if (savingPassword) return;
    const next = !isPasswordActive;
    if (next) {
      // Writing { isPasswordActive: true } without a hash would leave the
      // site serving fully open while the UI claims protection, so the
      // toggle is blocked until a password is set.
      if (newPassword === '' && !hasStoredHash) {
        setSetupError(true);
        setPasswordError(false);
        return;
      }
      if (newPassword !== '' && newPassword.length < 6) {
        setPasswordError(true);
        setSetupError(false);
        return;
      }
    }
    setSetupError(false);
    setPasswordError(false);
    setSavingPassword(true);
    setIsPasswordActive(next);
    // Status-only unless a valid new password was typed in this session;
    // the stored hash is left untouched otherwise.
    const fields = { isPasswordActive: next };
    if (newPassword.length >= 6) {
      const salt = bcrypt.genSaltSync(10);
      fields.encryptedPassword = bcrypt.hashSync(newPassword, salt);
    }
    try {
      await writePasswordFields(fields);
      toast.success(next ? 'Password protection is on.' : 'Password protection is off.');
    } catch (err) {
      console.error('Error updating password protection:', err);
      setIsPasswordActive(!next);
      toast.error('Could not update password protection. ' + errorLine(err));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSavePassword = async () => {
    if (savingPassword) return;
    setSetupError(false);
    if (newPassword.length < 6) {
      setPasswordError(true);
      return;
    }
    setPasswordError(false);
    setSavingPassword(true);
    try {
      const salt = bcrypt.genSaltSync(10);
      await writePasswordFields({
        encryptedPassword: bcrypt.hashSync(newPassword, salt),
        isPasswordActive,
      });
      toast.success('Password saved.');
    } catch (err) {
      console.error('Error saving password:', err);
      toast.error('Could not save password. ' + errorLine(err));
    } finally {
      setSavingPassword(false);
    }
  };

  // --- Favicon ---
  const faviconInputRef = useRef(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const handleFaviconChange = async (event) => {
    const input = event.target;
    const file = input.files && input.files[0];
    if (!file) return;
    setUploadingFavicon(true);
    try {
      const url = await uploadFaviconUrl(file, site.generatedUrl);
      await siteRef.set({ faviconUrl: url, updatedAt: new Date() }, { merge: true });
      await fetchSite();
      toast.success('Favicon saved.');
    } catch (err) {
      console.error('Error uploading favicon:', err);
      toast.error('Could not upload favicon. ' + errorLine(err));
    } finally {
      setUploadingFavicon(false);
      input.value = '';
    }
  };

  // --- Danger zone (delete flow moved from AeroSites, unchanged) ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Delete the site's version docs (batched) and then the url doc itself.
  // Blobs are NOT touched: they are shared content-addressed storage and
  // cleanup is a future garbage collector's job.
  const handleConfirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const versionsSnap = await siteRef.collection('versions').get();
      const docs = versionsSnap.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = db.batch();
        docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
      await siteRef.delete();
      navigate('/sites');
    } catch (err) {
      console.error('Error deleting site:', err);
      toast.error('Could not delete site. ' + errorLine(err));
      setDeleting(false);
    }
  };

  return (
    <div className="aero-sites-settings">
      {/* Site name — available to all users (naming your own site in your own
          dashboard is not paywalled). */}
      <div className="aero-card aero-card-pad">
        <div className="aero-section-label">Site name</div>
        <div className="aero-sites-field-row">
          <input
            className="aero-input"
            type="text"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveTitle();
            }}
            disabled={savingTitle}
          />
          <button
            type="button"
            className="aero-btn aero-btn-primary"
            onClick={saveTitle}
            disabled={savingTitle || !titleValue.trim()}
          >
            {savingTitle ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="aero-sites-field-note">
          Shown in your dashboard and in the browser tab of password pages.
        </p>
      </div>

      {/* Password protection */}
      <AeroFeatureGate
        requiredTier="basic"
        currentTier={subscriptionType}
        icon="lock"
        title="Password protection"
        message="Only people with the password can open the site."
        onUpgradeClick={openUpgradeModal}
      >
        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Password protection</div>
          <div className="aero-setting-row aero-sites-password-row">
            <div>
              <h4>Require a password to view this site</h4>
              <p>Viewers enter the password once and stay signed in for a week.</p>
            </div>
            <AeroSwitch
              checked={isPasswordActive}
              onChange={handlePasswordSwitch}
              disabled={savingPassword}
            />
          </div>
          <div className="aero-sites-field-row">
            <input
              className="aero-input"
              type="text"
              placeholder="Enter a new password to change it"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordError(false);
                setSetupError(false);
              }}
              disabled={savingPassword}
            />
            <button
              type="button"
              className="aero-btn aero-btn-primary"
              onClick={handleSavePassword}
              disabled={savingPassword}
            >
              {savingPassword ? 'Saving…' : 'Save'}
            </button>
          </div>
          {setupError && <p className="aero-sites-field-error">Set a password first.</p>}
          {passwordError && (
            <p className="aero-sites-field-error">
              Your password must be at least 6 characters long.
            </p>
          )}
        </div>
      </AeroFeatureGate>

      {/* Favicon */}
      <AeroFeatureGate
        requiredTier="basic"
        currentTier={subscriptionType}
        icon="sparkle"
        title="Your own favicon"
        message="Show your own icon in the browser tab."
        onUpgradeClick={openUpgradeModal}
      >
        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Favicon</div>
          <div className="aero-sites-favicon-row">
            {site.faviconUrl && (
              <img
                src={site.faviconUrl}
                alt="Current favicon"
                className="aero-sites-favicon-prev"
              />
            )}
            <button
              type="button"
              className="aero-btn aero-btn-secondary"
              onClick={() => faviconInputRef.current && faviconInputRef.current.click()}
              disabled={uploadingFavicon}
            >
              {uploadingFavicon
                ? 'Uploading…'
                : site.faviconUrl
                  ? 'Replace image'
                  : 'Upload image'}
            </button>
            <input
              type="file"
              accept="image/*"
              ref={faviconInputRef}
              onChange={handleFaviconChange}
              style={{ display: 'none' }}
            />
          </div>
          <p className="aero-sites-field-note">
            Use a square PNG, JPG or SVG, at least 70px by 70px.
          </p>
        </div>
      </AeroFeatureGate>

      {/* Site label — free/trial only. The "Made with Aero" badge is injected
          server-side (api/site.js) during the trial and auto-removed on any
          paid tier, so this is purely an info + upsell surface: it shows only
          for free/trial users and disappears once they upgrade. No backend,
          toggle or write — the badge removal already works server-side. */}
      {(subscriptionType === 'regular' || subscriptionType === 'free') && (
        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Site label</div>
          <p className="aero-sites-field-note">
            A “Made with Aero” badge shows on your site while you’re on the free plan.
          </p>
          <AeroUpsellBanner
            icon="badge"
            title="Remove the Aero badge"
            message="Your site shows without “Made with Aero” in the corner."
            onUpgradeClick={openUpgradeModal}
            inline
          />
        </div>
      )}

      {/* Site source (html sites only): the switch card stays in Settings for
          uploaded-files sites. For figma sites the source card and the
          breakpoints editor live in the Prototype tab instead. */}
      {site.type !== 'figma' && (
        <SiteSourceCard
          user={user}
          ownerUid={ownerUid}
          site={site}
          fetchSite={fetchSite}
          fetchVersions={fetchVersions}
          deploying={deploying}
          onDrop={onDrop}
        />
      )}

      {/* Danger zone */}
      <div className="aero-card aero-card-pad aero-sites-danger-card">
        <div className="aero-section-label aero-sites-danger-label">Danger zone</div>
        <div className="aero-setting-row aero-sites-danger-row">
          <div>
            <h4>Delete this site</h4>
            <p>Its versions and history will be removed. This cannot be undone.</p>
          </div>
          <button
            type="button"
            className="aero-btn aero-btn-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={deleting}
          >
            Delete site
          </button>
        </div>
      </div>

      {/* Delete confirm modal (portal, aero glass styling, moved from the
          AeroSites list page). */}
      {showDeleteModal &&
        ReactDOM.createPortal(
          <div
            className="ut-modal-root aero-modal-backdrop ut-theme"
            onClick={() => !deleting && setShowDeleteModal(false)}
          >
            <div className="aero-modal" onClick={(e) => e.stopPropagation()}>
              <div className="aero-modal-head">
                <h2>Delete {site.title || site.generatedUrl}?</h2>
                <button
                  type="button"
                  className="aero-btn aero-btn-ghost aero-btn-icon"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="aero-modal-body">
                <p style={{ margin: 0 }}>
                  Delete {site.title || site.generatedUrl}? Its versions and
                  history will be removed. This cannot be undone.
                </p>
              </div>
              <div className="aero-modal-foot">
                <button
                  type="button"
                  className="aero-btn aero-btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="aero-btn aero-btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete site'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default SiteSettingsTab;
